import { apiService } from '../services/apiService';
import { OnboardingProgressData } from '../types/api';
import {
  cleanEmail,
  setEmailOtpVerified,
  obSet,
  OB_KEYS,
  saveEncryptedBasicDetails,
  getSignupPasswordSession,
  safeSetItem,
} from './onboardingStorage';
import { updateHeaderData } from '../store/slices/uiSlice';
import { Dispatch } from 'redux';

/**
 * Restores storage and frontend state for all completed onboarding steps.
 * When a user logs in with an incomplete onboarding account (e.g. next_incomplete_step = 4),
 * this function restores the data and verification state for all previous completed steps (Steps 1, 2, 3...)
 * into local/session storage and Redux BEFORE navigating to next_incomplete_step.
 */
export const restoreOnboardingState = async (
  progressData: OnboardingProgressData,
  userEmail?: string,
  dispatch?: Dispatch
): Promise<void> => {
  if (!progressData || !progressData.steps) return;

  const resolvedEmail = cleanEmail(
    userEmail || progressData.user_email || localStorage.getItem('email') || ''
  );

  // 1. Restore email verification state if email_verified is true or 1 on backend
  const isEmailVerifiedOnBackend =
    progressData.email_verified === true ||
    (progressData as any).email_verified === 1 ||
    (progressData as any).email_verified === '1';

  if (isEmailVerifiedOnBackend) {
    if (resolvedEmail) {
      setEmailOtpVerified(resolvedEmail, true);
      localStorage.setItem('tnb_otp_email', resolvedEmail);
    }
    obSet(OB_KEYS.EMAIL_VERIFIED);
  }

  // 2. Cache progress response data in localStorage
  try {
    localStorage.setItem('onboarding_cached_progress', JSON.stringify(progressData));
  } catch (e) {
    console.warn('Failed to cache onboarding progress:', e);
  }

  // 3. Filter completed steps before next_incomplete_step (or all if onboarding complete)
  const nextIncompleteStep = progressData.next_incomplete_step || 5;
  const completedSteps = progressData.steps.filter(
    (step) => step.status_id === 1 && (progressData.is_complete || step.step_id < nextIncompleteStep)
  );

  // 4. Fetch/restore data for each completed step
  for (const step of completedSteps) {
    try {
      const stepId = step.step_id;
      const apiResponse = await apiService.getStepData(stepId);

      if (apiResponse?.status && apiResponse?.data) {
        const rawData =
          apiResponse.data.data !== undefined ? apiResponse.data.data : apiResponse.data;

        if (rawData && typeof rawData === 'object' && Object.keys(rawData).length > 0) {
          switch (stepId) {
            case 1: {
              // Step 1: Basic Details
              safeSetItem('onboarding_BasicDetail', rawData, 'restoreState');
              const legalName = rawData.full_name || rawData.legalName || rawData.name || '';
              const officialEmail = cleanEmail(
                rawData.email || rawData.officialEmail || resolvedEmail
              );
              const yourDesignation =
                rawData.designation_name || rawData.designation || rawData.yourDesignation || '';
              const yourDesignationId =
                rawData.designation_id || rawData.yourDesignationId || 0;
              const password = getSignupPasswordSession();

              await saveEncryptedBasicDetails({
                legalName,
                officialEmail,
                yourDesignation,
                yourDesignationId,
                password,
                termsAccepted: true,
              });

              if (dispatch && officialEmail) {
                dispatch(updateHeaderData({ email: officialEmail, source: 'Onboarding' }));
              }
              break;
            }

            case 2: {
              // Step 2: Company Details
              safeSetItem('onboarding_CompanyDetails', rawData, 'restoreState');
              obSet(OB_KEYS.COMPANY_DONE);
              const companyDetail = rawData.company_detail || rawData.companeyDetail || rawData;
              const companyName = companyDetail.company_name || companyDetail.name || rawData.company_name || rawData.name || '';
              const companyLogo = companyDetail.logo_image_url || companyDetail.company_img || companyDetail.company_logo || rawData.logo_image_url || rawData.company_img || rawData.company_logo || '';

              if (dispatch && (companyName || companyLogo)) {
                dispatch(
                  updateHeaderData({ companyName, companyLogo, source: 'Onboarding' })
                );
              }
              break;
            }

            case 3: {
              // Step 3: Company Intro / Looking For
              safeSetItem('onboarding_CompanyIntro', rawData, 'restoreState');
              safeSetItem('onboarding_LookingFor', rawData, 'restoreState');
              obSet(OB_KEYS.INTRO_DONE);
              break;
            }

            case 4: {
              // Step 4: Verification
              safeSetItem('onboarding_Verification', rawData, 'restoreState');
              if (rawData.domain_verified || rawData.domainVerified) {
                obSet(OB_KEYS.DOMAIN_VERIFIED);
              }
              if (rawData.gst_verified || rawData.gstVerified) {
                obSet(OB_KEYS.GST_VERIFIED);
              }
              break;
            }

            case 5: {
              // Step 5: Credits
              safeSetItem('onboarding_Credits', rawData, 'restoreState');
              break;
            }

            default:
              safeSetItem(`onboarding_Step${stepId}`, rawData, 'restoreState');
              break;
          }
        }
      }
    } catch (stepErr) {
      console.warn(`[restoreOnboardingState] Could not restore step ${step.step_id}:`, stepErr);
    }
  }
};
